// src/Admin/AdminPanel.tsx
import React, { useEffect, useState } from "react";
import { onAuthStateChanged, signOut, User } from "firebase/auth";
import { collection, doc, onSnapshot, query, where } from "firebase/firestore";
import {
    BarChart3, BookOpen, CalendarRange,
    BadgeIndianRupee, FileSpreadsheet, GraduationCap, Inbox, KeyRound, LogOut, MessageSquareText, ShieldCheck, UserCircle, UserRoundCheck
} from "lucide-react";
import { auth, db } from "../Lib/Firebase";
import "./admin-theme.css";

import { startOfWeek } from "./utils/date";
import SlotsTab from "./slots/SlotsTab";
import StudentsTab from "./students/StudentsTab";
import PartnersTab from "./partners/PartnersTab";
import AdminWorkspace from "./components/AdminWorkspace";
import AdminProfileTab, { AdminProfile } from "./profile/AdminProfileTab";
import StudentAnalyticsTab from "./analytics/StudentAnalyticsTab";
import FreeResourcesTab from "./resources/FreeResourcesTab";
import StudyMaterialsTab from "./materials/StudyMaterialsTab";
import BulkCandidateImportTab from "./students/BulkCandidateImportTab";
import CoursesTab from "./courses/CoursesTab";
import { FEATURES } from "../config/features";
import PaymentReviewTab from "./payments/PaymentReviewTab";
import AdminSecurityTab from "./security/AdminSecurityTab";
import AdminMessagesTab from "./messages/AdminMessagesTab";

type AdminTab = "profile" | "security" | "messages" | "slots" | "students" | "bulk-students" | "partners" | "courses" | "payments" | "analytics" | "feedback" | "resources" | "materials";

const adminTabs: { id: AdminTab; label: string; icon: React.ReactNode }[] = [
    { id: "profile", label: "My Profile", icon: <UserCircle className="h-4 w-4" /> },
    { id: "security", label: "Security", icon: <KeyRound className="h-4 w-4" /> },
    { id: "messages", label: "Messages", icon: <Inbox className="h-4 w-4" /> },
    ...(FEATURES.mockInterviews ? [
        { id: "slots" as const, label: "Mock Interview Slots", icon: <CalendarRange className="h-4 w-4" /> },
    ] : []),
    { id: "students", label: "Students", icon: <GraduationCap className="h-4 w-4" /> },
    { id: "bulk-students", label: "Bulk Candidate Import", icon: <FileSpreadsheet className="h-4 w-4" /> },
    { id: "partners", label: "Partners", icon: <UserRoundCheck className="h-4 w-4" /> },
    { id: "courses", label: "Courses", icon: <BookOpen className="h-4 w-4" /> },
    { id: "payments", label: "Payment Review", icon: <BadgeIndianRupee className="h-4 w-4" /> },
    { id: "analytics", label: "Student Analytics", icon: <BarChart3 className="h-4 w-4" /> },
    ...(FEATURES.mockInterviews ? [{ id: "feedback" as const, label: "Feedback", icon: <MessageSquareText className="h-4 w-4" /> }] : []),
    { id: "resources", label: "Free Resources", icon: <BookOpen className="h-4 w-4" /> },
    { id: "materials", label: "Study Materials", icon: <BookOpen className="h-4 w-4" /> },
];

export default function AdminPanel() {
    const [user, setUser] = useState<User | null>(null);
    const [activeTab, setActiveTab] = useState<AdminTab>("profile");
    const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
    const [adminProfile, setAdminProfile] = useState<AdminProfile>({});
    const [unreadMessages, setUnreadMessages] = useState(0);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, (u) => setUser(u));
        return () => unsub();
    }, []);

    useEffect(() => {
        const email = user?.email?.toLowerCase();
        if (!email) return;
        return onSnapshot(doc(db, "admins", email), (snapshot) => setAdminProfile(snapshot.data() as AdminProfile || {}));
    }, [user]);

    useEffect(() => onSnapshot(query(collection(db, "adminMessages"), where("status", "==", "UNREAD")), (snapshot) => setUnreadMessages(snapshot.size), () => setUnreadMessages(0)), []);

    const doSignOut = async () => {
        await signOut(auth);
    };

    const currentSection = adminTabs.find((tab) => tab.id === activeTab)?.label || "Administration";

    return (
        <div className="mockzo-admin min-h-screen overflow-x-hidden bg-[radial-gradient(900px_500px_at_10%_-10%,#bae6fd_0%,transparent_60%),radial-gradient(800px_500px_at_100%_0%,#dbeafe_0%,transparent_58%),linear-gradient(180deg,#f8fbff_0%,#eef6ff_100%)] text-slate-900">
            {/* Header */}
            <header className="sticky top-0 z-40 border-b border-blue-300/40 bg-gradient-to-r from-[#061a4a] via-[#075dcc] to-[#38aee8] px-3 py-3 text-white shadow-xl shadow-blue-950/15 sm:px-5">
                <div className="mx-auto flex max-w-[1500px] items-center justify-between gap-4">
                    <a href="/" className="flex items-center gap-3" aria-label="MockZo home">
                        <span className="grid h-10 w-10 place-items-center rounded-xl bg-white shadow-md"><img src="/images/jpg/mockzo-logo-v3.webp" alt="MockZo logo" className="h-8 w-8" /></span>
                        <span className="leading-tight"><span className="block text-lg font-black tracking-wide">MockZo</span><span className="block text-[10px] font-bold uppercase tracking-[0.22em] text-blue-100">Admin Portal</span></span>
                    </a>
                    <div className="flex min-w-0 items-center gap-2 sm:gap-3">
                        <div className="hidden min-w-0 text-right sm:block">
                            <div className="max-w-[240px] truncate text-sm font-bold">{adminProfile.name || user?.displayName || user?.email}</div>
                            <div className="text-[11px] font-semibold text-blue-100">{adminProfile.title || "Administrator"}</div>
                        </div>
                        <div className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-xl border border-white/30 bg-white/15 text-sm font-black shadow-inner">
                            {adminProfile.photoUrl ? <img src={adminProfile.photoUrl} alt="Administrator" className="h-full w-full object-cover" /> : (adminProfile.name?.[0] || user?.displayName?.[0] || user?.email?.[0] || "A").toUpperCase()}
                        </div>
                        <button onClick={doSignOut} aria-label="Sign out" className="inline-flex items-center gap-2 rounded-xl border border-white/25 bg-white/10 px-3 py-2 text-sm font-bold text-white transition hover:bg-white/20">
                            <LogOut className="h-4 w-4" /> <span className="hidden md:inline">Sign out</span>
                        </button>
                    </div>
                </div>
            </header>

            {/* Layout */}
            <div className="mx-auto grid max-w-[1500px] grid-cols-1 gap-4 px-3 py-4 sm:px-5 sm:py-6 lg:grid-cols-[270px,minmax(0,1fr)] lg:gap-6">
                <aside className="flex min-w-0 flex-col overflow-hidden self-start rounded-3xl border border-blue-100 bg-white/90 p-3 shadow-xl shadow-blue-900/5 backdrop-blur lg:sticky lg:top-[82px] lg:h-[calc(100dvh-104px)]">
                    <div className="mb-3 flex shrink-0 items-center gap-2 px-2 py-1 text-xs font-black uppercase tracking-[0.16em] text-blue-700"><ShieldCheck className="h-4 w-4" />Administration</div>
                    <nav aria-label="Admin sections" className="flex min-h-0 snap-x gap-2 overflow-x-auto pb-1 lg:flex-1 lg:snap-none lg:flex-col lg:overflow-x-hidden lg:overflow-y-auto lg:overscroll-contain lg:pr-1 lg:pb-1">{adminTabs.map((tab) => <button key={tab.id} aria-current={activeTab === tab.id ? "page" : undefined} className={`inline-flex shrink-0 snap-start items-center gap-2.5 whitespace-nowrap rounded-xl px-3 py-2.5 text-left text-sm font-semibold transition lg:w-full ${activeTab === tab.id ? "bg-gradient-to-r from-[#075dcc] to-[#38aee8] text-white shadow-md shadow-blue-500/20" : "border border-transparent text-slate-600 hover:border-blue-100 hover:bg-blue-50 hover:text-blue-800"}`} onClick={() => setActiveTab(tab.id)}><span className={activeTab === tab.id ? "text-white" : "text-blue-600"}>{tab.icon}</span><span>{tab.label}</span>{tab.id === "messages" && unreadMessages > 0 && <span aria-label={`${unreadMessages} unread messages`} className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-black ${activeTab === tab.id ? "bg-white text-blue-700" : "bg-red-100 text-red-700"}`}>{unreadMessages > 99 ? "99+" : unreadMessages}</span>}</button>)}</nav>
                </aside>

                <section className="min-w-0 space-y-4">
                    <div className="overflow-hidden rounded-3xl bg-gradient-to-r from-[#061a4a] via-[#075dcc] to-[#38aee8] px-5 py-5 text-white shadow-xl shadow-blue-900/10 sm:px-7">
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-100">MockZo control centre</p>
                        <h1 className="mt-1 text-2xl font-black sm:text-3xl">{currentSection}</h1>
                        <p className="mt-1 text-sm text-blue-100">Manage MockZo operations, student outcomes and publishing controls.</p>
                    </div>
                    <div className="admin-workspace min-w-0">
                    {activeTab === "profile" ? <AdminProfileTab user={user} onSaved={setAdminProfile} />
                    : activeTab === "security" ? <AdminSecurityTab user={user} />
                    : activeTab === "messages" ? <AdminMessagesTab />
                    : activeTab === "slots" ? (
                        <SlotsTab
                            weekStart={weekStart}
                            setWeekStart={setWeekStart}
                        />
                    ) : activeTab === "students" ? <StudentsTab />
                    : activeTab === "bulk-students" ? <BulkCandidateImportTab />
                    : activeTab === "partners" ? <PartnersTab />
                    : activeTab === "courses" ? <CoursesTab />
                    : activeTab === "payments" ? <PaymentReviewTab />
                    : activeTab === "analytics" ? <StudentAnalyticsTab />
                    : activeTab === "feedback" ? <AdminWorkspace title="Feedback" description="Review interviewer feedback and publish approved feedback to student dashboards." actions={["Pending reviews", "Published feedback", "Quality checks", "Student history"]} />
                    : activeTab === "resources" ? <FreeResourcesTab />
                    : <StudyMaterialsTab />}
                    </div>
                </section>
            </div>
        </div>
    );
}
